import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiResponse } from 'app/core/models/date-range-preset.models';
import { withIdempotencyKey } from 'app/core/utils/idempotency.util';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import {
  CompleteScbInstallationRequest,
  CreateScbCostAssignmentRequest,
  CreateScbPaymentProofRequest,
  ExecuteScbBillingRunRequest,
  LinkDocumentRequest,
  ReviewScbPaymentProofRequest,
  ScbBillingRun,
  ScbCostAssignment,
  ScbDeal,
  ScbDealListQuery,
  ScbDealProfit,
  ScbEntitlement,
  ScbInstallation,
  ScbMyBillingPendingInvoiceDetail,
  ScbMyBillingPendingInvoices,
  ScbPackage,
  ScbPayMyBillingInvoiceRequest,
  ScbPayMyBillingInvoiceResult,
  ScbSubmitMyBillingPaymentProofRequest,
  ScbSubmitMyBillingPaymentProofResult,
  ScbPaymentProof,
  ScbRecoveryQuery,
  ScbRecoveryRow,
  ScbServiceContract,
  ScbSettings,
  UpsertScbDealRequest,
  UpsertScbPackageRequest,
  UpsertScbSettingsRequest,
} from './scb.models';

function toHttpParams(
  query: object | Record<string, string | number | boolean | null | undefined> | undefined
): HttpParams | undefined {
  if (!query) return undefined;
  let params = new HttpParams();
  let hasAny = false;
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    params = params.set(key, String(value));
    hasAny = true;
  }
  return hasAny ? params : undefined;
}

@Injectable({ providedIn: 'root' })
export class ScbApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}scb`;

  getSettings(): Observable<ApiResponse<ScbSettings | null>> {
    return this.http.get<ApiResponse<ScbSettings | null>>(`${this.base}/settings`);
  }

  /** Guide: PUT or POST `/api/scb/settings` — FE uses PUT. */
  upsertSettings(
    body: UpsertScbSettingsRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbSettings>> {
    return this.http.put<ApiResponse<ScbSettings>>(
      `${this.base}/settings`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  getPackages(activeOnly = true): Observable<ApiResponse<ScbPackage[]>> {
    return this.http.get<ApiResponse<ScbPackage[]>>(`${this.base}/packages`, {
      params: toHttpParams({ activeOnly }),
    });
  }

  getPackage(id: string): Observable<ApiResponse<ScbPackage>> {
    return this.http.get<ApiResponse<ScbPackage>>(`${this.base}/packages/${id}`);
  }

  createPackage(
    body: UpsertScbPackageRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbPackage>> {
    return this.http.post<ApiResponse<ScbPackage>>(
      `${this.base}/packages`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  updatePackage(
    id: string,
    body: UpsertScbPackageRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbPackage>> {
    return this.http.put<ApiResponse<ScbPackage>>(
      `${this.base}/packages/${id}`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  deletePackage(id: string, idempotencyKey?: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.base}/packages/${id}`,
      withIdempotencyKey(idempotencyKey)
    );
  }

  getDeals(query?: ScbDealListQuery): Observable<ApiResponse<ScbDeal[]>> {
    return this.http.get<ApiResponse<ScbDeal[]>>(`${this.base}/deals`, {
      params: toHttpParams(query),
    });
  }

  getDeal(id: string): Observable<ApiResponse<ScbDeal>> {
    return this.http.get<ApiResponse<ScbDeal>>(`${this.base}/deals/${id}`);
  }

  createDeal(
    body: UpsertScbDealRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbDeal>> {
    return this.http.post<ApiResponse<ScbDeal>>(
      `${this.base}/deals`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  updateDeal(
    id: string,
    body: UpsertScbDealRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbDeal>> {
    return this.http.put<ApiResponse<ScbDeal>>(
      `${this.base}/deals/${id}`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  lockDeal(id: string, idempotencyKey?: string): Observable<ApiResponse<ScbDeal>> {
    return this.http.post<ApiResponse<ScbDeal>>(
      `${this.base}/deals/${id}/lock`,
      {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  cancelDeal(id: string, idempotencyKey?: string): Observable<ApiResponse<ScbDeal>> {
    return this.http.post<ApiResponse<ScbDeal>>(
      `${this.base}/deals/${id}/cancel`,
      {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  getInstallation(id: string): Observable<ApiResponse<ScbInstallation>> {
    return this.http.get<ApiResponse<ScbInstallation>>(`${this.base}/installations/${id}`);
  }

  getInstallationByDeal(dealId: string): Observable<ApiResponse<ScbInstallation>> {
    return this.http.get<ApiResponse<ScbInstallation>>(
      `${this.base}/installations/by-deal/${dealId}`
    );
  }

  startInstallation(id: string, idempotencyKey?: string): Observable<ApiResponse<ScbInstallation>> {
    return this.http.post<ApiResponse<ScbInstallation>>(
      `${this.base}/installations/${id}/start`,
      {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  completeInstallation(
    id: string,
    body?: CompleteScbInstallationRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbInstallation>> {
    return this.http.post<ApiResponse<ScbInstallation>>(
      `${this.base}/installations/${id}/complete`,
      body ?? {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  addInstallationTask(
    id: string,
    title: string,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbInstallation>> {
    return this.http.post<ApiResponse<ScbInstallation>>(
      `${this.base}/installations/${id}/tasks`,
      { title },
      withIdempotencyKey(idempotencyKey)
    );
  }

  completeInstallationTask(
    id: string,
    taskId: string,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbInstallation>> {
    return this.http.post<ApiResponse<ScbInstallation>>(
      `${this.base}/installations/${id}/tasks/${taskId}/complete`,
      {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  linkInstallationDocument(
    id: string,
    body: LinkDocumentRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.base}/installations/${id}/links`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  getContracts(status?: number | null): Observable<ApiResponse<ScbServiceContract[]>> {
    return this.http.get<ApiResponse<ScbServiceContract[]>>(`${this.base}/contracts`, {
      params: toHttpParams({ status }),
    });
  }

  getContract(id: string): Observable<ApiResponse<ScbServiceContract>> {
    return this.http.get<ApiResponse<ScbServiceContract>>(`${this.base}/contracts/${id}`);
  }

  suspendContract(
    id: string,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbServiceContract>> {
    return this.http.post<ApiResponse<ScbServiceContract>>(
      `${this.base}/contracts/${id}/suspend`,
      {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  reactivateContract(
    id: string,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbServiceContract>> {
    return this.http.post<ApiResponse<ScbServiceContract>>(
      `${this.base}/contracts/${id}/reactivate`,
      {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  syncContractStatus(idempotencyKey?: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.base}/contracts/sync-status`,
      {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  syncContractPaid(idempotencyKey?: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.base}/contracts/sync-paid`,
      {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  executeBillingRun(
    body?: ExecuteScbBillingRunRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbBillingRun>> {
    return this.http.post<ApiResponse<ScbBillingRun>>(
      `${this.base}/billing-runs/execute`,
      body ?? {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  getBillingRuns(take = 50): Observable<ApiResponse<ScbBillingRun[]>> {
    return this.http.get<ApiResponse<ScbBillingRun[]>>(`${this.base}/billing-runs`, {
      params: toHttpParams({ take }),
    });
  }

  getBillingRun(id: string): Observable<ApiResponse<ScbBillingRun>> {
    return this.http.get<ApiResponse<ScbBillingRun>>(`${this.base}/billing-runs/${id}`);
  }

  getEntitlement(tenantCompanyId: string): Observable<ApiResponse<ScbEntitlement>> {
    return this.http.get<ApiResponse<ScbEntitlement>>(
      `${this.base}/entitlement/${tenantCompanyId}`
    );
  }

  /** Tenant (JWT company) open / overdue invoices for soft-lock banner CTA. */
  getMyBillingPendingInvoices(): Observable<ApiResponse<ScbMyBillingPendingInvoices>> {
    return this.http.get<ApiResponse<ScbMyBillingPendingInvoices>>(
      `${this.base}/my-billing/pending-invoices`
    );
  }

  /**
   * Tenant (JWT company) single pending invoice with full nested sales-invoice payload.
   * Do not use Sales Invoice module routes for tenants — this is service-contract billing.
   */
  getMyBillingPendingInvoiceDetail(
    salesInvoiceId: string
  ): Observable<ApiResponse<ScbMyBillingPendingInvoiceDetail>> {
    return this.http.get<ApiResponse<ScbMyBillingPendingInvoiceDetail>>(
      `${this.base}/my-billing/pending-invoices/${encodeURIComponent(salesInvoiceId)}`
    );
  }

  /**
   * Tenant pays a subscription invoice — posts expense voucher (Dr expense / Cr cash-bank)
   * and creates a payment proof in the ops inbox (platform AR still cleared via Customer Receipt).
   */
  payMyBillingInvoice(
    body: ScbPayMyBillingInvoiceRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbPayMyBillingInvoiceResult>> {
    return this.http.post<ApiResponse<ScbPayMyBillingInvoiceResult>>(
      `${this.base}/my-billing/pay-invoice`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  /** Tenant attaches payment slip only (no tenant GL) — after pay or as optional proof. */
  submitMyBillingPaymentProof(
    body: ScbSubmitMyBillingPaymentProofRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbSubmitMyBillingPaymentProofResult>> {
    return this.http.post<ApiResponse<ScbSubmitMyBillingPaymentProofResult>>(
      `${this.base}/my-billing/payment-proofs`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  getPaymentProofs(status?: number | null): Observable<ApiResponse<ScbPaymentProof[]>> {
    return this.http.get<ApiResponse<ScbPaymentProof[]>>(`${this.base}/payment-proofs`, {
      params: toHttpParams({ status }),
    });
  }

  createPaymentProof(
    body: CreateScbPaymentProofRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbPaymentProof>> {
    return this.http.post<ApiResponse<ScbPaymentProof>>(
      `${this.base}/payment-proofs`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  approvePaymentProof(
    id: string,
    body?: ReviewScbPaymentProofRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbPaymentProof>> {
    return this.http.post<ApiResponse<ScbPaymentProof>>(
      `${this.base}/payment-proofs/${id}/approve`,
      body ?? {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  rejectPaymentProof(
    id: string,
    body?: ReviewScbPaymentProofRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbPaymentProof>> {
    return this.http.post<ApiResponse<ScbPaymentProof>>(
      `${this.base}/payment-proofs/${id}/reject`,
      body ?? {},
      withIdempotencyKey(idempotencyKey)
    );
  }

  getRecovery(query?: ScbRecoveryQuery): Observable<ApiResponse<ScbRecoveryRow[]>> {
    return this.http.get<ApiResponse<ScbRecoveryRow[]>>(`${this.base}/analytics/recovery`, {
      params: toHttpParams(query),
    });
  }

  getDealProfit(dealId: string): Observable<ApiResponse<ScbDealProfit>> {
    return this.http.get<ApiResponse<ScbDealProfit>>(
      `${this.base}/analytics/profit/${dealId}`
    );
  }

  getCostAssignments(dealId: string): Observable<ApiResponse<ScbCostAssignment[]>> {
    return this.http.get<ApiResponse<ScbCostAssignment[]>>(
      `${this.base}/analytics/cost-assignments`,
      { params: toHttpParams({ dealId }) }
    );
  }

  createCostAssignment(
    body: CreateScbCostAssignmentRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<ScbCostAssignment>> {
    return this.http.post<ApiResponse<ScbCostAssignment>>(
      `${this.base}/analytics/cost-assignments`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }
}
